import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";

/** A group heading's slot when expanded: a hairline, then the title. */
export const GROUP_HEADING_HEIGHT_PX = 28;
/**
 * In the rail the title is gone, so the slot shrinks to the hairline and a little
 * air either side. Every group the rail saves this way is ~half a row, which is
 * most of what lets the whole icon menu fit on a laptop screen without scrolling.
 */
export const RAIL_GROUP_RULE_HEIGHT_PX = 13;

type SideMenuGroupHeadingProps = {
  title: string;
  collapsed: boolean;
};

/**
 * "FRONT DESK", "BILLING", … above each group, under a hairline that separates it
 * from the group before. The rail is icons only, so it keeps just the hairline in
 * a shorter slot; each row's hover card names its group instead (ENG-2535).
 */
export default function SideMenuGroupHeading({
  title,
  collapsed
}: SideMenuGroupHeadingProps) {
  const theme = useTheme();
  const rule = {
    height: "1px",
    backgroundColor: alpha(theme.palette.common.white, 0.16)
  };

  if (collapsed) {
    return (
      <Box
        aria-hidden="true"
        data-testid="side-menu-group-rule"
        sx={{
          height: `${RAIL_GROUP_RULE_HEIGHT_PX}px`,
          boxSizing: "border-box",
          pt: "6px",
          px: 1.25
        }}
      >
        <Box sx={rule} />
      </Box>
    );
  }

  return (
    <Box
      data-testid="side-menu-group-heading"
      sx={{
        height: `${GROUP_HEADING_HEIGHT_PX}px`,
        boxSizing: "border-box",
        pt: 1,
        px: 0.75
      }}
    >
      <Box sx={{ ...rule, mb: "6px" }} />
      <Typography
        component="div"
        variant="overline"
        sx={{
          display: "block",
          px: 0.5,
          fontSize: "12px",
          lineHeight: "13px",
          fontWeight: "fontWeightBold",
          letterSpacing: "0.06em",
          whiteSpace: "nowrap",
          color: alpha(theme.palette.common.white, 0.62)
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}
